import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Activity, AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import Checkbox from "@app/component/input/Checkbox";
import UserMultiSelectField from "@app/component/input/UserMultiSelectField";
import useView from "@app/component/view/utils/useView";
import { getMe } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import messages from "./messages";
import styles from "./UserFilter.module.scss";

interface Props {
  currentUserId: User["id"];
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
  users: User[];
}

const UserFilter: React.FC<Props> = ({ currentUserId, subscribe, unsubscribe, users }) => {
  const intl = useIntl();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Activity>();

  const handleChangeAssigneeList = useCallback(
    (userIds: Array<User["id"]>) => {
      updateFilterModel({
        assignee: { operator: FilterOperator.IN_ANY, value: userIds },
      });
    },
    [updateFilterModel]
  );

  const handleChangeShowOnlyMy = useCallback(
    (onlyMy: boolean) => {
      updateFilterModel({
        user: onlyMy ? { operator: FilterOperator.IN_ANY, value: [currentUserId] } : undefined,
      });
    },
    [currentUserId, updateFilterModel]
  );

  const assigneeIds = useMemo(
    () =>
      filter.assignee &&
      isSimpleCondition(filter.assignee) &&
      filter.assignee.operator === FilterOperator.IN_ANY
        ? (filter.assignee.value as Array<User["id"]>)
        : [],
    [filter]
  );

  useEffect(
    () => {
      if (assigneeIds.length === 0) {
        updateFilterModel({
          assignee: { operator: FilterOperator.IN_ANY, value: [currentUserId] },
        });
      }
    },
    // this effect should be called once during initialization.
    // If there is no users in filter - we need to put current user there
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId, updateFilterModel]
  );

  useEffect(() => {
    const listener = {
      onAdd: (type: EntityType, activities: AnyEntity[]) => {
        // add new activities assignees to filter
        const newAssigneeIds = (activities as Activity[])
          .map(({ assignee }) => assignee?.id)
          .filter((id) => isDefined(id) && !assigneeIds.includes(id));

        if (newAssigneeIds.length) {
          updateFilterModel({
            assignee: {
              operator: FilterOperator.IN_ANY,
              value: [...assigneeIds, ...newAssigneeIds],
            },
          });
        }
      },
    };
    subscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    };
  }, [assigneeIds, subscribe, unsubscribe, updateFilterModel]);

  const onlyMy = useMemo(
    () =>
      filter.user &&
      isSimpleCondition(filter.user) &&
      filter.user.operator === FilterOperator.IN_ANY &&
      Array.isArray(filter.user.value) &&
      filter.user.value.length === 1 &&
      filter.user.value[0] === currentUserId,
    [currentUserId, filter]
  );

  const noAssigneeSelected = assigneeIds.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.title}>{intl.formatMessage(messages.title)}</div>
      <div className={cn(styles.subTitle, { [styles.noAssigneeSelected]: noAssigneeSelected })}>
        {intl.formatMessage(
          noAssigneeSelected ? messages.subTitleNoAssigneeSelected : messages.subTitle
        )}
      </div>
      <UserMultiSelectField
        onChange={handleChangeAssigneeList}
        size="middle"
        users={users}
        value={assigneeIds}
      />
      <Checkbox checked={onlyMy} onChange={handleChangeShowOnlyMy}>
        {intl.formatMessage(messages.onlyMyActivities)}
      </Checkbox>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getMe(state)!.id,
  users: getUsers(state),
});

const mapDispatchToProps = {
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserFilter);
